import React from 'react';
import { ResourceCenterArticleProvider } from './useResourceCenterArticle';
import GlobalMain from 'molecules/Main/GlobalMain';
import { graphql, PageProps, HeadProps } from 'gatsby';
import { HeadComponent } from 'atoms/Head/head';
import { ResourceContentfulModel } from 'src/api/models/Resources';
import { ResourceArticleTiny } from 'src/api/models/Resources';
import HeroBannerSection from 'sections/ResourceCenterArticle/HeroBanner/HeroBannerSection';
import ArticleBodySection from 'sections/ResourceCenterArticle/ArticleBody/ArticleBodySection';
import CallToActionSection from 'sections/ResourceCenterArticle/CallToAction/CallToActionSection';
import JoinCommunitySection from 'sections/ResourceCenterArticle/JoinCommunity/JoinCommunitySection';
import MoreLikeThisSection from 'sections/ResourceCenterArticle/MoreLikeThis/MoreLikeThisSection';
import CategoryMenuSection from 'sections/ResourceCenterArticle/CategoryMenu/CategoryMenu';
import calculateEstimatedReadingTime from 'src/functions/calculateEstimatedReadingTime';

interface ResourceCenterArticleTemplatePageContextProps {
  slug: string;
  category: string;
  subCategory: string;
  hasTableOfContents: boolean;
}

interface ResourceCenterArticleTemplateProps extends PageProps<unknown, ResourceCenterArticleTemplatePageContextProps> {
  data: {
    contentfulResource: ResourceContentfulModel;
    allContentfulResource: {
      edges: { node: ResourceArticleTiny }[];
    };
  };
}

const ResourceCenterArticleTemplate = ({
  pageContext,
  data,
  location,
}: ResourceCenterArticleTemplateProps): JSX.Element => {
  return (
    <ResourceCenterArticleProvider
      article={data.contentfulResource}
      otherArticles={data.allContentfulResource.edges.map((edge) => edge.node)}
      hasTableOfContents={pageContext.hasTableOfContents}
    >
      <GlobalMain location={location}>
        <HeroBannerSection />
        <ArticleBodySection />
        <CallToActionSection />
        <MoreLikeThisSection />
        <JoinCommunitySection />
        <CategoryMenuSection />
      </GlobalMain>
    </ResourceCenterArticleProvider>
  );
};

export default ResourceCenterArticleTemplate;

export const Head = (
  props: HeadProps<ResourceCenterArticleTemplateProps['data'], ResourceCenterArticleTemplatePageContextProps>
): JSX.Element => {
  const articleData = props.data.contentfulResource;
  const bodyWords = articleData.body?.childMarkdownRemark?.wordCount?.words || 0;
  const bodyOverflowWords = articleData.bodyOverflow?.childMarkdownRemark?.wordCount?.words || 0;
  const estimatedReadingTime = calculateEstimatedReadingTime(bodyWords + bodyOverflowWords);

  const helmet = {
    title: articleData.seoTitle || articleData.title,
    description: articleData.seoDescription || articleData.description?.description,
    previewImageURL: articleData.heroImage?.file?.url,
    author: articleData.author ?? 'The Mighty Team',
    estimatedReadingTime: estimatedReadingTime,
  };

  return (
    <HeadComponent
      helmet={helmet}
      location={props.location}
    />
  );
};

export const pageQuery = graphql`
  query ResourceCenterBySlug($slug: String!, $category: String!) {
    contentfulResource(slug: { eq: $slug }) {
      ...ResourceContent
    }
    allContentfulResource(limit: 20, filter: { category: { eq: $category }, slug: { ne: "attachments-testing" } }) {
      edges {
        node {
          ...ResourceContentTiny
        }
      }
    }
  }
`;
